import * as React from 'react';
import type { FieldFormProps } from '@helpcenter/field-types';
import { FieldSingleSelect, FormMultiSelect } from '@helpcenter/form-select';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { ValueType } from '@atlaskit/select';

import { toMultiSelectValue } from '@atlassian/help-center-common-util/select';
import type { CmdbObject } from '../../../rest/cmdb-object-picker';
import { CmdbSelectOption } from '../cmdb-object-picker-option';

import messages from './messages';

export interface CmdbObjectOptionGroup {
	options: CmdbObjectPickerOption[];
	label: string;
}

export interface CmdbObjectPickerOption {
	label: CmdbObject['label'];
	value: CmdbObject['id'];
	cmdbObject: CmdbObject;
}

export type InputActionTypes = 'set-value' | 'input-change' | 'input-blur' | 'menu-close';

export interface InputActionMeta {
	action: InputActionTypes;
}

export interface CmdbObjectPickerDumbProps extends FieldFormProps<string[]> {
	label: string;
	isLoading?: boolean;
	doInitialSearch?: boolean; // Whether we do an initial loadOptions("")
	isMulti: boolean;
	filterAttributes: string[];
	limit: number;
	hasValidConfig: boolean;
	onInputChange: (input: string, type: InputActionMeta) => void;
	options: CmdbObjectOptionGroup[];
	onFocus: () => void;
	// When cleaning up jsm_assets_field_only_display_label_in_portal change this defaultValue
	// prop name to be "selectedValues", so more clearly indicate that its now a controlled component
	defaultValue: CmdbObjectPickerOption[] | undefined;
	populateDefaultValue: boolean;
	classNamePrefix?: string;
	onMenuScrollToBottom: () => Promise<void>;
}

export class CmdbObjectPickerDumb extends React.Component<
	CmdbObjectPickerDumbProps & WrappedComponentProps
> {
	onChangeForSelect = (selectedOption: ValueType<CmdbObjectPickerOption>) => {
		this.props.onChange?.(
			this.props.id,
			toMultiSelectValue(selectedOption).map((v) => v.value),
		);
	};

	customComponents = {
		Option: CmdbSelectOption,
	};

	noOptionsMessage = () => {
		const { intl } = this.props;
		return intl.formatMessage(messages.noOptions);
	};

	render() {
		const {
			isMulti,
			disabled,
			doInitialSearch,
			intl,
			defaultValue,
			populateDefaultValue,
			...otherProps
		} = this.props;

		const placeholder = intl.formatMessage(messages.searchPlaceholder);

		const makeControlledAndOnlyDisplayLabel = FeatureGates.checkGate(
			'jsm_assets_field_only_display_label_in_portal',
		);
		let selectedValues = defaultValue;
		if (makeControlledAndOnlyDisplayLabel && Array.isArray(defaultValue)) {
			// We change the shape of the selected values to display just the object label rather than the full searchable label
			selectedValues = defaultValue
				.filter((value) => value !== undefined)
				.map((value) => ({
					...value,
					label: value.cmdbObject.label,
				}));
		}

		if (isMulti) {
			return (
				<FormMultiSelect
					{...otherProps}
					actionSubjectId="cmdbObjectPickerField"
					placeholder={placeholder}
					noOptionsMessage={this.noOptionsMessage}
					onChange={this.onChangeForSelect}
					components={this.customComponents}
					value={
						populateDefaultValue || makeControlledAndOnlyDisplayLabel ? selectedValues : undefined
					}
				/>
			);
		}

		let singleSelectValue: CmdbObjectPickerOption | null | undefined =
			selectedValues && selectedValues[0];
		if (makeControlledAndOnlyDisplayLabel) {
			// Need to explicitly set to `null` rather than `undefined` to clear the value.
			singleSelectValue = selectedValues && selectedValues.length > 0 ? selectedValues[0] : null;
		}
		return (
			<FieldSingleSelect
				{...otherProps}
				actionSubjectId="cmdbObjectPickerField"
				placeholder={placeholder}
				noOptionsMessage={this.noOptionsMessage}
				onChange={this.onChangeForSelect}
				components={this.customComponents}
				value={
					(populateDefaultValue || makeControlledAndOnlyDisplayLabel) && selectedValues
						? singleSelectValue
						: undefined
				}
				isClearable
			/>
		);
	}
}

export default injectIntl(CmdbObjectPickerDumb);
